import { MapPin, Star, Phone, Globe, Clock, DollarSign, ExternalLink } from "lucide-react";
import type { ToolWidgetProps } from "./index";

interface Hours {
  open_now?: boolean;
  weekday_descriptions?: string[];
  periods?: Array<{
    open_day: number;
    open_time: string;
    close_day: number;
    close_time: string;
  }>;
}

interface Place {
  google_place_id: string;
  name: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  types?: string[];
  primary_type?: string;
  primary_type_display?: string;
  phone?: string;
  international_phone?: string;
  website?: string;
  google_maps_url?: string;
  business_status?: string;
  rating?: number;
  rating_count?: number;
  price_level?: number;
  description?: string;
  hours?: Hours;
  current_hours?: Hours;
}

interface GooglePlacesDetailsData {
  place: Place;
  success: boolean;
}

function PriceLevel({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(4)].map((_, i) => (
        <DollarSign
          key={i}
          className={`w-3 h-3 ${i < level ? "text-green-600" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

export function GooglePlacesDetailsWidget({
  data,
}: ToolWidgetProps<GooglePlacesDetailsData>) {
  const place = data?.place;

  if (!place) {
    return (
      <div className="text-sm text-slate-500 italic">Aucune information trouvée</div>
    );
  }

  const hours = place.current_hours || place.hours;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-800">{place.name}</h3>
          {place.rating && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium text-slate-700">
                {place.rating.toFixed(1)}
              </span>
              {place.rating_count && (
                <span className="text-sm text-slate-400">
                  ({place.rating_count})
                </span>
              )}
            </div>
          )}
        </div>
        {place.primary_type_display && (
          <div className="text-sm text-slate-500 mt-1">
            {place.primary_type_display}
          </div>
        )}
      </div>

      {/* Description */}
      {place.description && (
        <p className="text-sm text-slate-600">{place.description}</p>
      )}

      {/* Details Grid */}
      <div className="space-y-2 text-sm">
        {place.formatted_address && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700">{place.formatted_address}</span>
          </div>
        )}

        {hours && (
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              {hours.weekday_descriptions ? (
                <div className="space-y-0.5">
                  {hours.weekday_descriptions.map((desc, i) => (
                    <div key={i} className="text-slate-700">{desc}</div>
                  ))}
                </div>
              ) : (
                <span className="text-slate-700">Horaires non disponibles</span>
              )}
              {hours.open_now !== undefined && (
                <span
                  className={`ml-2 text-xs px-2 py-0.5 rounded ${
                    hours.open_now
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {hours.open_now ? "Ouvert" : "Fermé"}
                </span>
              )}
            </div>
          </div>
        )}

        {place.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-400" />
            <a
              href={`tel:${place.phone}`}
              className="text-default-600 hover:text-default-700"
            >
              {place.phone}
            </a>
          </div>
        )}

        {place.website && (
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" />
            <a
              href={place.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-default-600 hover:text-default-700 truncate"
            >
              {place.website}
            </a>
          </div>
        )}

        {place.google_maps_url && (
          <div className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-slate-400" />
            <a
              href={place.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-default-600 hover:text-default-700"
            >
              Voir sur Google Maps
            </a>
          </div>
        )}

        {place.price_level && (
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Prix</span>
            <PriceLevel level={place.price_level} />
          </div>
        )}
      </div>
    </div>
  );
}
